/**
 * FHIR R4 Bundle Builder — assembles a complete FHIR Bundle from Arete data.
 */

import {
  buildPatient,
  buildObservation,
  buildPHQ4Response,
  buildPHQ4ScoreObservation,
  buildDiagnosticReport,
  buildPhenotypeObservation,
  labMarkerToLoincKey,
} from './builders';

interface DailySummary {
  date: string;
  hrv?: number | null;
  lowest_hr?: number | null;
  total_sleep_mins?: number | null;
  rem_sleep_mins?: number | null;
  light_sleep_mins?: number | null;
  steps?: number | null;
  exercise_mins?: number | null;
  bedtime_start?: string | null;
}

interface LabRow {
  marker: string;
  value: number;
  unit: string;
  ref_low: number | null;
  ref_high: number | null;
  date: string;
}

interface PHQ4Row {
  id: string;
  q1: number;
  q2: number;
  q3: number;
  q4: number;
  score: number;
  created_at: string;
}

interface PhenotypeRow {
  id: string;
  name: string;
  score: number;
  created_at?: string;
}

/**
 * Build a complete FHIR R4 Bundle containing all of a patient's Arete data
 * for a given date range.
 */
export function buildAreteBundle(params: {
  profile: { id: string; full_name?: string | null; dob?: string | null };
  dailySummaries: DailySummary[];
  labs: LabRow[];
  phq4Entries: PHQ4Row[];
  phenotypes: PhenotypeRow[];
  startDate: string;
  endDate: string;
}) {
  const { profile, dailySummaries, labs, phq4Entries, phenotypes, endDate } = params;
  const patientId = `patient-${profile.id}`;
  const entries: Array<{ fullUrl: string; resource: { resourceType: string; id: string; [key: string]: unknown } }> = [];
  let obsCounter = 0;

  const nextId = (prefix: string) => `${prefix}-${++obsCounter}`;

  // 1. Patient
  const patient = buildPatient(profile);
  entries.push({ fullUrl: `urn:uuid:${patient.id}`, resource: patient });

  // 2. Wearable observations (one per metric per day)
  for (const day of dailySummaries) {
    const dt = `${day.date}T00:00:00Z`;
    const device = 'Device/oura-ring';

    if (day.hrv != null && day.hrv > 0) {
      const obs = buildObservation({ id: nextId('obs'), patientId, loincKey: 'hrv_sdnn', category: 'vital-signs', value: day.hrv, effectiveDateTime: dt, deviceReference: device });
      if (obs) entries.push({ fullUrl: `urn:uuid:${obs.id}`, resource: obs });
    }
    if (day.lowest_hr != null && day.lowest_hr > 0) {
      const obs = buildObservation({ id: nextId('obs'), patientId, loincKey: 'resting_hr', category: 'vital-signs', value: day.lowest_hr, effectiveDateTime: dt, deviceReference: device });
      if (obs) entries.push({ fullUrl: `urn:uuid:${obs.id}`, resource: obs });
    }
    if (day.total_sleep_mins != null && day.total_sleep_mins > 0) {
      const period = day.bedtime_start ? {
        start: day.bedtime_start,
        end: new Date(new Date(day.bedtime_start).getTime() + day.total_sleep_mins * 60000).toISOString(),
      } : undefined;
      const obs = buildObservation({ id: nextId('obs'), patientId, loincKey: 'sleep_duration', category: 'vital-signs', value: day.total_sleep_mins, effectivePeriod: period, effectiveDateTime: period ? undefined : dt, deviceReference: device });
      if (obs) entries.push({ fullUrl: `urn:uuid:${obs.id}`, resource: obs });
    }
    if (day.steps != null && day.steps > 0) {
      const obs = buildObservation({ id: nextId('obs'), patientId, loincKey: 'daily_steps', category: 'activity', value: day.steps, effectiveDateTime: dt });
      if (obs) entries.push({ fullUrl: `urn:uuid:${obs.id}`, resource: obs });
    }
    if (day.exercise_mins != null && day.exercise_mins > 0) {
      const obs = buildObservation({ id: nextId('obs'), patientId, loincKey: 'exercise_minutes', category: 'activity', value: Math.round(day.exercise_mins), effectiveDateTime: dt });
      if (obs) entries.push({ fullUrl: `urn:uuid:${obs.id}`, resource: obs });
    }
  }

  // 3. Lab observations + DiagnosticReport
  const labObsIds: string[] = [];
  const labDates = new Set<string>();
  for (const lab of labs) {
    const loincKey = labMarkerToLoincKey(lab.marker);
    if (!loincKey) continue;
    const id = nextId('lab');
    const interp: 'N' | 'H' | 'L' | undefined =
      lab.ref_low != null && lab.value < lab.ref_low ? 'L' :
      lab.ref_high != null && lab.value > lab.ref_high ? 'H' : 'N';
    const obs = buildObservation({
      id, patientId, loincKey, category: 'laboratory', value: lab.value,
      effectiveDateTime: `${lab.date}T00:00:00Z`,
      referenceRange: lab.ref_low != null || lab.ref_high != null
        ? { low: lab.ref_low ?? undefined, high: lab.ref_high ?? undefined, unit: lab.unit }
        : undefined,
      interpretation: interp,
    });
    if (obs) {
      entries.push({ fullUrl: `urn:uuid:${obs.id}`, resource: obs });
      labObsIds.push(id);
      labDates.add(lab.date);
    }
  }

  // One DiagnosticReport per blood draw date
  for (const date of Array.from(labDates)) {
    const dateLabIds = labObsIds.filter((_, i) => labs[i]?.date === date);
    if (dateLabIds.length > 0) {
      const report = buildDiagnosticReport({ id: nextId('report'), patientId, effectiveDateTime: `${date}T00:00:00Z`, observationIds: dateLabIds });
      entries.push({ fullUrl: `urn:uuid:${report.id}`, resource: report });
    }
  }

  // 4. PHQ-4 entries
  for (const entry of phq4Entries) {
    const qrId = nextId('qr');
    const qr = buildPHQ4Response({ id: qrId, patientId, authored: entry.created_at, answers: [entry.q1, entry.q2, entry.q3, entry.q4] });
    entries.push({ fullUrl: `urn:uuid:${qr.id}`, resource: qr });

    const scoreObs = buildPHQ4ScoreObservation({ id: nextId('phq4score'), patientId, score: entry.score, effectiveDateTime: entry.created_at, questionnaireResponseId: qrId });
    entries.push({ fullUrl: `urn:uuid:${scoreObs.id}`, resource: scoreObs });
  }

  // 5. Phenotype observations
  for (const pheno of phenotypes) {
    const obs = buildPhenotypeObservation({
      id: nextId('phenotype'),
      patientId,
      patternId: pheno.id,
      patternName: pheno.name,
      score: pheno.score,
      effectiveDateTime: pheno.created_at || endDate,
    });
    entries.push({ fullUrl: `urn:uuid:${obs.id}`, resource: obs });
  }

  return {
    resourceType: 'Bundle',
    id: `arete-bundle-${profile.id}-${endDate}`,
    type: 'collection',
    timestamp: new Date().toISOString(),
    meta: { lastUpdated: new Date().toISOString() },
    entry: entries,
  };
}
